import * as React from 'react';

import type {EntityAttribute, EntityAttributeValue} from 'nutshell-graphql-types';

import type {FieldError} from '../../../../../../ui/core-entity/hooks/use-custom-field-handlers';

import {WidgetErrorBanner} from '../../card/card-widget/widget-error-banner';
import {CoreEntityConfirmationDialog} from '../../core-entity-confirmation-dialog/core-entity-confirmation-dialog';
import {useManageSaveState} from '../../saved-setting-toast/use-manage-save-state';

import {BaseRow} from '../base-row/base-row';
import type {RowType} from '../types';

import {CustomFieldRowContent} from './custom-field-row-content';
import {getCustomFieldRowType, getToolbarOptions} from './helpers';

export type ClickToCallPropsType = {
    phoneCallerType: string | null | undefined;
    entityId: string;
    anchorElementId?: string;
    handleCall: (
        phoneNumberFormatted: string,
        phoneNumberRaw: string,
        phoneCallerType: string,
        entityId: string,
        anchorElementId: string
    ) => void;
};

type Props = {
    field: EntityAttribute;
    value: EntityAttributeValue | null | undefined;
    handleTextFieldUpdate: (
        newValue: string | null | undefined,
        field: EntityAttribute
    ) => Promise<any>;
    handleEnumUpdate: (
        newValue: string[] | null | undefined,
        field: EntityAttribute
    ) => Promise<any>;
    handleCurrencyUpdate: (
        currencyShortname: string,
        value: number | null | undefined,
        field: EntityAttribute
    ) => Promise<any>;
    handleAddressUpdate: (
        address1: string,
        address2: string,
        address3: string,
        city: string,
        state: string,
        postalCode: string,
        country: string,
        field: EntityAttribute
    ) => Promise<any>;
    handlePhoneUpdate: (
        numberFormattedWithCountryCode: string,
        extension: string,
        field: EntityAttribute
    ) => Promise<any>;
    clickToCallProps?: ClickToCallPropsType | null | undefined;
    error: string | null | undefined;
    setErrorForField: (fieldError: FieldError) => void;
};

export function CustomFieldRow(props: Props) {
    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const [isClearConfirmationOpen, setIsClearConfirmationOpen] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [clickToCallPopoverOpen, setClickToCallPopoverOpen] = React.useState<boolean>(false);
    const {toastState, saveFailed} = useManageSaveState(isSaving, 1000);

    const clearButtonRef = React.useRef<HTMLElement | null | undefined>(null);

    React.useEffect(() => {
        const eventName = 'closeReactClickToCallPopover';
        const eventListener = () => {
            setClickToCallPopoverOpen(false);
        };

        $(window).on(eventName, eventListener);

        return () => {
            $(window).off(eventName, eventListener);
        };
    }, []);

    const ctcProps = {
        anchorElementId: `tertiary-action-button-${props.field.id}`,
        ...props.clickToCallProps,
    } as const;

    const handleClear = () => {
        setIsSaving(true);
        props.handleTextFieldUpdate(null, props.field).finally(() => setIsSaving(false));
    };

    const handleClearClicked = () => {
        setIsClearConfirmationOpen(true);
    };

    const getClearButtonRef = (ref?: HTMLElement | null) => {
        clearButtonRef.current = ref;
    };

    const toolbarOptions = getToolbarOptions(
        props.field,
        props.value,
        // @ts-expect-error
        ctcProps,
        setIsEditing,
        handleClearClicked,
        getClearButtonRef,
        setClickToCallPopoverOpen
    );

    const rowType: RowType = React.useMemo(() => {
        return getCustomFieldRowType(props.field);
    }, [props.field]);

    React.useEffect(() => {
        if (props.error) {
            setIsSaving(false);
            saveFailed();
        }
    }, [props.error, saveFailed]);

    const setError = (error?: string | null) => {
        props.setErrorForField({
            fieldName: props.field.attribute.name,
            error: error,
        });
    };

    return (
        <>
            {props.error && (
                <div className='pb-2'>
                    <WidgetErrorBanner error={props.error} setError={setError} />
                </div>
            )}
            <BaseRow
                type={rowType}
                title={props.field.attribute.name || 'Custom field'}
                saveToastState={toastState}
                toolbarProps={{
                    options: toolbarOptions,
                    forceHideToolbar: isEditing,
                    forceShowToolbar: isClearConfirmationOpen || clickToCallPopoverOpen,
                }}
                onClick={() => setIsEditing(true)}
                tooltipText={props.field.attribute.description}
            >
                <CustomFieldRowContent
                    field={props.field}
                    value={props.value}
                    handleTextFieldUpdate={props.handleTextFieldUpdate}
                    handleEnumUpdate={props.handleEnumUpdate}
                    handleCurrencyUpdate={props.handleCurrencyUpdate}
                    handleAddressUpdate={props.handleAddressUpdate}
                    handlePhoneUpdate={props.handlePhoneUpdate}
                    setIsSaving={setIsSaving}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                />
            </BaseRow>
            {isClearConfirmationOpen && (
                <CoreEntityConfirmationDialog
                    anchor={clearButtonRef.current}
                    type='customField'
                    parentEntity='lead'
                    onConfirm={() => {
                        setIsClearConfirmationOpen(false);
                        handleClear();
                    }}
                    onCancel={() => {
                        setIsClearConfirmationOpen(false);
                    }}
                />
            )}
        </>
    );
}
